/* eslint-disable react/prop-types */
import React, { useMemo, useContext, useState, useEffect } from "react";
import TableLayout from "./table-layout";
import PropTypes from "prop-types";
import { useSortBy, useTable } from "react-table";
import { OldOrderDetailsData, OrderDetailsData } from "../../contexts";
import ReOrderModal from "../reorder-form/reorder-modal";
import { toast } from "react-toastify";
import "../../styles/Tableinstance.scss";
import { request } from "../../config/helpers/axios-instance";
import { BsFillTrashFill } from 'react-icons/bs'
import { MdModeEditOutline } from "react-icons/md"
import { useNavigate } from "react-router-dom";
import { getOrderStatuses } from "../../utils/helpers";
import { ALL_ORDER_TYPE, DIGITIZE_ORDER_TYPE, ORDER_CANCELLED, PRESORTED, PRESORTED_ORDER_TYPE, STRIPS_ORDER_TYPE, TENANT_COMPANY_MAP } from "../../utils/constants.js";
import CancellationReasonPrompt from "../CancellationReasonPrompt";
import { useSourcesData } from "../../hooks/fetch-hooks/get-sources";
import { getTenantId } from "../../utils/helpers";

// eslint-disable-next-line no-undef
const Constants = require("./Constants");

// Cell / Cell2 / CellChannel / CellSource are exported (alongside the default
// TableInstance export below) so src/v2/pages/orders-legacy can compose its own,
// wider column set using these EXACT renderers — same status-cell (with its
// CancellationReasonPrompt handling), same channel/source selects, same Action
// buttons — rather than forking them. Purely additive: no existing import of this
// module changes behavior.
export const Cell = (row, reOrderModalClick, handleDeleteRow, OrderType) => {
    const navigate = useNavigate();
    const { setOldOrder } = React.useContext(OldOrderDetailsData);

    return (
        <div className="w-full h-full flex flex-row gap-[3px] items-center">
            {
                Constants.COLUMNS_FUCTIONALITIES[OrderType].includes("Reorder") &&
                <div className="flex flex-row items-center">
                    <button
                        id="create-order"
                        className="px-4 py-2 rounded-2xl font-sans create-order"
                        onClick={() => {
                            if (row.row.original.addressId == "0") {
                                toast.error('Please add address first')
                            } else {
                                setOldOrder(row.row.original);
                                reOrderModalClick();
                            }
                        }}>
                        ReOrder
                    </button>
                </div>
            }
            {Constants.COLUMNS_FUCTIONALITIES[OrderType].includes("Delete") && < button
                id="create-order"
                className="px-4 py-2 rounded-2xl font-sans create-order bg-[red]/[0.85] disabled:opacity-50"
                disabled={row?.cell?.row?.original?.payments?.filter(payment => payment.status === 'SUCCESS' || payment.status === 'PENDING').length > 0}
                onClick={async () => {
                    if (confirm("Do you wish to delete the order")) {
                        const res = await request({
                            url: `/userOrder/admin/id/${row?.cell?.row?.values?.id}`,
                            method: "DELETE",
                        })
                        if (res && res.data.success) {
                            // Here we have to delete the row
                            handleDeleteRow(row?.cell?.row?.values?.id);
                        }
                    }
                }}>
                <BsFillTrashFill />
            </button>}
            {Constants.COLUMNS_FUCTIONALITIES[OrderType].includes("Edit") && < a rel="noreferrer" className="px-4 py-2 rounded-2xl bg-blue-500 cursor-pointer" onClick={() => navigate(`/order/edit/${row?.cell?.row?.values?.id}?orderType=${row?.cell?.row?.original?.type?.includes(PRESORTED) ? PRESORTED_ORDER_TYPE : STRIPS_ORDER_TYPE}`)}>
                <MdModeEditOutline className=" text-[white] text-xl" />
            </a>}

        </div >
    );

};

// Order Status Column with ts Functions
//
// A real component rather than a plain render function, because selecting the cancelled
// status has to HOLD the change while a reason is collected — that needs local state, and
// Cell2 below is invoked as `Cell: (row) => Cell2(...)`, i.e. called during the parent's
// render, where hooks cannot live.
const OrderStatusCell = ({ row, type, types, refetchOrders }) => {
    const currentStatus = row?.row?.original?.status;
    const [value, setValue] = useState(currentStatus);
    const [pendingCancel, setPendingCancel] = useState(false);
    const [saving, setSaving] = useState(false);

    // Re-sync after a refetch, so the select reflects what the server actually stored.
    useEffect(() => { setValue(currentStatus); }, [currentStatus]);

    const submit = async (nextValue, extra = {}) => {
        setSaving(true);
        try {
            const response = await request({
                method: "PUT",
                url: `/userOrder/admin/id/${row.row.original.id}`,
                data: type === 'status'
                    ? { status: nextValue, ...extra }
                    : { type: nextValue }
            })

            if (!response.data.success) {
                toast.warn("Please Try After Sometime")
                setValue(currentStatus)
            }
            else {
                setPendingCancel(false)
                refetchOrders()
            }
        }
        catch (err) {
            toast.warn("Please Try After Sometime")
            setValue(currentStatus)
        }
        finally {
            setSaving(false)
        }
    };

    const handleChange = (e) => {
        const next = e.target.value;
        setValue(next);

        // Intercept: nothing is sent until ops supplies a reason.
        if (type === 'status' && next === ORDER_CANCELLED) {
            setPendingCancel(true);
            return;
        }

        setPendingCancel(false);
        submit(next);
    };

    return (
        // `relative` anchors the cancellation popover, which floats out of this cell —
        // the column is far too narrow to lay the prompt out in flow.
        <div className="relative px-2 text-black">
            <select
                style={{
                    border: "1px solid #b0b5ba",
                    borderRadius: "6px"
                }}
                className="w-[100%] "
                value={value}
                disabled={saving}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onChange={handleChange}>
                {
                    types?.map((option) => {
                        return (
                            <option key={option} value={option}>{option}</option>
                        )
                    })
                }
            </select>
            {pendingCancel && (
                <CancellationReasonPrompt
                    saving={saving}
                    onConfirm={(reason) => submit(ORDER_CANCELLED, reason ? { cancelledReason: reason } : {})}
                    onDismiss={() => {
                        // Revert the select — the order was never actually cancelled.
                        setPendingCancel(false);
                        setValue(currentStatus);
                    }}
                />
            )}
        </div>
    );
};

export const Cell2 = (row, type, types, refetchOrders) => (
    <OrderStatusCell row={row} type={type} types={types} refetchOrders={refetchOrders} />
);

// Channel Cell
export const CellChannel = (row, type, channels, refetchOrders) => {
    return (
        <div className="px-2 text-black">
            <select
                style={{
                    border: "1px solid #b0b5ba",
                    borderRadius: "6px"
                }}
                className="w-[100%] "
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onChange={async (e) => {
                    try {
                        const response =
                            await request({
                                method: "PUT",
                                url: `/userOrder/admin/id/${row.row.original.id}`,
                                data: type === 'channel' ? {
                                    channel: e.target.value
                                } : {
                                    type: e.target.value
                                }
                            })

                        if (!response.data.success) {
                            toast.warn("Please Try After Sometime")
                        }
                        else {
                            refetchOrders()
                        }
                    }
                    catch (err) {
                        toast.warn("Please Try After Sometime")
                    }
                }}>
                <option value="">Select Channel</option>
                {
                    channels?.map((value) => {
                        return (
                            <option selected={row?.row?.original?.channel === value} key={value} value={value}>{value}</option>
                        )
                    })
                }
            </select>
        </div>
    );
};

// Source Cell
export const CellSource = (row, type, sources, refetchOrders) => {
    return (
        <div className="px-2 text-black">
            <select
                style={{
                    border: "1px solid #b0b5ba",
                    borderRadius: "6px"
                }}
                className="w-[100%] "
                onClick={(e) => {
                    e.stopPropagation();
                }}
                disabled={!row?.row?.original?.channel}
                onChange={async (e) => {
                    try {
                        const response =
                            await request({
                                method: "PUT",
                                url: `/userOrder/admin/id/${row.row.original.id}`,
                                data: type === 'source' ? {
                                    source: e.target.value
                                } : {
                                    type: e.target.value
                                }
                            })

                        if (!response.data.success) {
                            toast.warn("Please Try After Sometime")
                        }
                        else {
                            refetchOrders()
                        }
                    }
                    catch (err) {
                        toast.warn("Please Try After Sometime")
                    }
                }}>
                <option value="">Select Source</option>
                {
                    sources?.map((value) => {
                        return (
                            <option selected={row?.row?.original?.source === value} key={value} value={value}>{value}</option>
                        )
                    })
                }
            </select>
        </div>
    );
};

function TableInstance({ tableData, changeRightBarOpen, pageCount, setPageSort, pageSort, handleDeleteRow, refetchOrders, fetchAllOrders }) {
    const orderStatuses = getOrderStatuses();
    const { channels, getSourcesForChannels } = useSourcesData();
    const { OrderType, isReorder } = useContext(OrderDetailsData);
    const [modal, showModal] = React.useState(false);
    const reOrderModalClick = () => {
        showModal(!modal);
    };
    const [oldOrder, setOldOrder] = React.useState(null);
    const isEazy = getTenantId() === TENANT_COMPANY_MAP["EAZY"];
    const [columns, data] = useMemo(() => {
        let columns;
        switch (isReorder ? 'ReOrder' : OrderType) {
            case PRESORTED_ORDER_TYPE:
                columns = Constants.ORDERS_COLUMN
                columns = [
                    ...columns,
                    {
                        Header: "Status",
                        accessor: "status",
                        Cell: (row) => Cell2(row, 'status', orderStatuses[PRESORTED_ORDER_TYPE], refetchOrders)
                    },
                    {
                        Header: "Channel",
                        accessor: "channel",
                        Cell: (row) => CellChannel(row, 'channel', channels, refetchOrders)
                    },
                    {
                        Header: "Source",
                        accessor: "source",
                        Cell: (row) => CellSource(row, 'source', getSourcesForChannels(row?.row?.original?.channel), refetchOrders)
                    },
                ];
                break;
            case STRIPS_ORDER_TYPE:
                columns = Constants.ORDERS_COLUMN
                columns = [
                    ...columns,
                    {
                        Header: "Status",
                        accessor: "status",
                        Cell: (row) => Cell2(row, 'status', orderStatuses[STRIPS_ORDER_TYPE], refetchOrders)
                    },
                    {
                        Header: "Channel",
                        accessor: "channel",
                        Cell: (row) => CellChannel(row, 'channel', channels, refetchOrders)
                    },
                    {
                        Header: "Source",
                        accessor: "source",
                        Cell: (row) => CellSource(row, 'source', getSourcesForChannels(row?.row?.original?.channel), refetchOrders)
                    },
                ];
                break;
            case ALL_ORDER_TYPE:
                columns = Constants.ORDERS_COLUMN
                columns = [
                    ...columns,
                    {
                        Header: "Status",
                        accessor: "status",
                        Cell: (row) => {
                            return Cell2(row, 'status', row?.cell?.row?.original?.type?.includes(PRESORTED) ? orderStatuses[PRESORTED_ORDER_TYPE] : orderStatuses[STRIPS_ORDER_TYPE], refetchOrders)
                        }
                    },
                    {
                        Header: "Channel",
                        accessor: "channel",
                        Cell: (row) => CellChannel(row, 'channel', channels, refetchOrders)
                    },
                    {
                        Header: "Source",
                        accessor: "source",
                        Cell: (row) => CellSource(row, 'source', getSourcesForChannels(row?.row?.original?.channel), refetchOrders)
                    },
                ];
                break;
            case 'ReOrder':
                columns = Constants.REORDERS_COLUMN
                break;
            case DIGITIZE_ORDER_TYPE:
                columns = Constants.DIGITIZE_COLUMN
                columns = [
                    ...columns,
                    {
                        Header: "Status",
                        accessor: "status",
                        Cell: (row) => Cell2(row, 'status', orderStatuses[DIGITIZE_ORDER_TYPE], refetchOrders)
                    },

                ];
                break;
            default:
                columns = Constants.ORDERS_COLUMN
                break;
        }
        if (isEazy) {
            columns = columns.filter(
                (col) => col.accessor !== 'evitalMedicineAmount' && col.accessor !== 'serviceChargeAmount'
            );
            columns = [
                ...columns,
                {
                    Header: "MRN",
                    accessor: "mrn",
                    Cell: (row) => {
                        const mrno = row.row.original.metadata?.mrno;
                        return mrno ? <span>{mrno}</span> : <span className="text-gray-400">—</span>;
                    }
                }
            ];
        }
        columns = columns.map((col) => {
            if (col.accessor === 'inventoryId') {
                return {
                    ...col,
                    Cell: (row) => {
                        const invId = row.value;
                        const billNo = row.row.original.additionalDetails?.billNumber;
                        if (!invId) return <span className="text-gray-400">—</span>;
                        return <span>{invId}{billNo ? ` (${billNo})` : ''}</span>;
                    },
                };
            }
            if (col.accessor === 'evitalMedicineAmount') {
                return {
                    ...col,
                    Cell: (row) => {
                        const items = row.row.original.paymentItems || [];
                        const amt = items
                            .filter((item) => item.invoiceType === 'MEDICINE')
                            .reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
                        return <span>{amt > 0 ? `₹${amt}` : '—'}</span>;
                    },
                };
            }
            if (col.accessor === 'serviceChargeAmount') {
                return {
                    ...col,
                    Cell: (row) => {
                        const items = row.row.original.paymentItems || [];
                        // Net service charge: non-medicine invoices only. Exclude credit notes
                        // (reversals) and reversed statuses so this matches the order's Total.
                        const REVERSED = ['CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED'];
                        const amt = items
                            .filter((item) => item.invoiceType !== 'MEDICINE'
                                && item.documentType !== 'CREDIT_NOTE'
                                && !REVERSED.includes(item.status))
                            .reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
                        return <span>{amt > 0 ? `₹${amt}` : '—'}</span>;
                    },
                };
            }
            return col;
        });
        columns = [
            ...columns,
            {
                Header: "Action",
                accessor: "action",
                Cell: (row) => Cell(row, reOrderModalClick, handleDeleteRow, OrderType),
            },
        ];
        return [columns, tableData];
    }, [tableData]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: { sortBy: pageSort },
            manualSortBy: true,
            autoResetSortBy: false,
            autoResetExpanded: false,
            autoResetPage: false,
        },
        useSortBy
    );



    return (
        <OldOrderDetailsData.Provider value={{ oldOrder, setOldOrder }}>
            {oldOrder && oldOrder.addressId != "0" && (
                <ReOrderModal
                    oldOrderDetails={oldOrder}
                    value={modal}
                    action={reOrderModalClick}
                    id={oldOrder.id}
                />
            )}

            <TableLayout {...tableInstance}
                changeRightBarOpen={changeRightBarOpen}
                pageCount={pageCount}
                setPageSort={setPageSort}
                refetchOrders={refetchOrders}
                fetchAllOrders={fetchAllOrders}
            />
        </OldOrderDetailsData.Provider>
    );
}

export default TableInstance;

TableInstance.propTypes = {
    tableData: PropTypes.array.isRequired,
    changeRightBarOpen: PropTypes.func.isRequired,
    pageCount: PropTypes.number,
    setPageSort: PropTypes.any,
    pageSort: PropTypes.any,
    RowSelectHandler: PropTypes.any,
    SelectedRows: PropTypes.any
    // setOrderChanged: PropTypes.any
};

Cell.propTypes = {
    row: PropTypes.any,
};